<template>
  <UnsavedChangesModal ref="unsavedChangesModal" />

  <h2 class="text-primary mx-2">{{ $t('page.manage-tenants.form.title') }}</h2>
  <div class="container-fluid" v-if="isSystemAdmin || isTenantAdmin">
    <div class="row">
      <div class="col-4">
        <div class="mel-divider d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.tenant') }}</p>
        </div>
        <Form ref="tenantFormRef" @submit="onSubmitTenant" :validation-schema="tenantSchema" v-slot="{ errors, isSubmitting }" class="m-2">
          <div class="mb-3">
            <label for="code" class="form-label">{{ $t('field.generic.code') }}</label>
            <Field id="code" name="code" type="text" class="form-control" :class="{ 'is-invalid': errors.code }" maxlength="20" :modelValue="selectedTenant?.code" :disabled="selectedTenantId !== 0" />
            <div class="invalid-feedback" v-if="!!errors.code">{{ errors.code }}</div>
          </div>
          <div class="mb-3">
            <label for="name" class="form-label">{{ $t('field.generic.name') }}</label>
            <Field id="name" name="name" type="text" class="form-control" :class="{ 'is-invalid': errors.name }" maxlength="40" :modelValue="selectedTenant?.name" />
            <div class="invalid-feedback" v-if="!!errors.name">{{ errors.name }}</div>
          </div>
          <div class="d-flex justify-content-between">
            <button type="submit" class="btn btn-primary me-auto" :disabled="isSubmitting">
              <span v-if="selectedTenantId !== 0">
                <font-awesome-icon :icon="['fas', 'floppy-disk']" />
                {{ $t('action.save') }}
              </span>
              <span v-if="selectedTenantId === 0">
                <font-awesome-icon :icon="['fas', 'plus']" />
                {{ $t('action.create') }}
              </span>
            </button>
          </div>
          <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>
        </Form>
      </div>
      <div class="col-4" v-if="selectedTenantId === 0"></div>
      <div class="col-4" v-else>
        <div class="mel-divider mel-divider-success d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.site') }}</p>
        </div>
        <Form ref="siteFormRef" @submit="onSubmitSite" :validation-schema="siteSchema" v-slot="{ errors, isSubmitting }" class="m-2">
          <div class="mb-3">
            <label for="code" class="form-label">{{ $t('field.generic.code') }}</label>
            <Field id="code" name="code" type="text" class="form-control" :class="{ 'is-invalid': errors.code }" maxlength="20" :modelValue="selectedSite?.code" :disabled="selectedSiteId !== 0" />
            <div class="invalid-feedback" v-if="!!errors.code">{{ errors.code }}</div>
          </div>
          <div class="mb-3">
            <label for="name" class="form-label">{{ $t('field.generic.name') }}</label>
            <Field id="name" name="name" type="text" class="form-control" :class="{ 'is-invalid': errors.name }" maxlength="40" :modelValue="selectedSite?.name" />
            <div class="invalid-feedback" v-if="!!errors.name">{{ errors.name }}</div>
          </div>
          <div class="mb-3">
            <label for="companyName" class="form-label">{{ $t('field.generic.company') }}</label>
            <Field id="companyName" name="companyName" type="text" class="form-control" :class="{ 'is-invalid': errors.companyName }" maxlength="100" :modelValue="selectedSite?.info.companyName" />
            <div class="invalid-feedback" v-if="!!errors.companyName">{{ errors.companyName }}</div>
          </div>
          <div class="mb-3">
            <label for="address" class="form-label">{{ $t('field.generic.address') }}</label>
            <Field id="address" name="address" type="text" as="textarea" rows="4" class="form-control" :class="{ 'is-invalid': errors.address }" maxlength="400" :modelValue="selectedSite?.info.address" />
            <div class="invalid-feedback" v-if="!!errors.address">{{ errors.address }}</div>
          </div>
          <div class="mb-3">
            <label for="contactName" class="form-label">{{ $t('field.generic.contactName') }}</label>
            <Field id="contactName" name="contactName" type="text" class="form-control" :class="{ 'is-invalid': errors.contactName }" maxlength="100" :modelValue="selectedSite?.info.contactName" />
            <div class="invalid-feedback" v-if="!!errors.contactName">{{ errors.contactName }}</div>
          </div>
          <div class="mb-3">
            <label for="email" class="form-label">{{ $t('field.generic.email') }}</label>
            <Field id="email" name="email" type="text" class="form-control" :class="{ 'is-invalid': errors.email }" maxlength="256" :modelValue="selectedSite?.info.email" />
            <div class="invalid-feedback" v-if="!!errors.email">{{ errors.email }}</div>
          </div>
          <div class="mb-3">
            <label for="phone" class="form-label">{{ $t('field.generic.phone') }}</label>
            <Field id="phone" name="phone" type="text" class="form-control" :class="{ 'is-invalid': errors.phone }" maxlength="20" :modelValue="selectedSite?.info.phone" />
            <div class="invalid-feedback" v-if="!!errors.phone">{{ errors.phone }}</div>
          </div>
          <div class="d-flex justify-content-between">
            <button type="submit" class="btn btn-primary me-auto" :disabled="isSubmitting">
              <span v-if="selectedSiteId !== 0">
                <font-awesome-icon :icon="['fas', 'floppy-disk']" />
                {{ $t('action.save') }}
              </span>
              <span v-if="selectedSiteId === 0">
                <font-awesome-icon :icon="['fas', 'plus']" />
                {{ $t('action.create') }}
              </span>
            </button>
          </div>
          <div v-if="errors.apiError" class="alert alert-danger mt-3 mb-0">{{ errors.apiError }}</div>
        </Form>
      </div>
      <div class="col-4 bg-body-tertiary border border-secondary-subtle border-end-0 rounded-start pb-3">
        <!-- selectable tenants -->
        <div class="mel-divider d-flex align-items-center my-3">
          <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.tenants', userTenants.length) }}</p>
        </div>
        <ul class="nav nav-pills flex-column">
          <li v-if="isSystemAdmin">
            <a @click="select_tenant(0)" href="#" class="nav-link text-decoration-none fst-italic"
              :class="{ 'active bg-melodi text-light': selectedTenantId === 0, 'text-melodi': selectedTenantId !== 0 }">
              <font-awesome-layers class="fa-1x me-2">
                <font-awesome-icon :icon="['fas', 'building']" />
              </font-awesome-layers>
              <span class="ms-1 text">{{ $t('action.newEllipsis' )}}</span>
            </a>
          </li>
          <li v-for="tenant in sortedTenants" :key="tenant.id">
            <a @click="select_tenant(tenant.id)" href="#" class="nav-link text-decoration-none"
              :class="{ 'active': selectedTenantId === tenant.id }">
              <font-awesome-layers class="fa-1x me-2">
                <font-awesome-icon :icon="['fas', 'building']" />
              </font-awesome-layers>
              <span class="ms-1 text">{{ tenant.name }}</span>
            </a>
          </li>
        </ul>
        <!-- selectable sites (dependent on tenant selection) -->
        <div v-if="selectedTenantId !== 0">
          <div class="mel-divider mel-divider-success d-flex align-items-center my-3">
            <p class="text-center fw-bold mx-3 mb-0 text-primary">{{ $t('global.sites', tenantFilteredUserSites.length) }}</p>
          </div>
          <ul class="nav nav-pills flex-column">
            <li v-if="isSystemAdmin || isTenantAdmin">
              <a @click="select_site(0)" href="#" class="nav-link text-decoration-none fst-italic"
              :class="{ 'active bg-melodi text-light': selectedSiteId === 0, 'text-melodi': selectedSiteId !== 0 }">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'warehouse']" />
                </font-awesome-layers>
                <span class="ms-1 text">{{ $t('action.newEllipsis' )}}</span>
              </a>
            </li>
            <li v-for="site in tenantFilteredUserSites" :key="site.id">
              <a @click="select_site(site.id)" href="#" class="nav-link text-decoration-none"
                :class="{ 'active': selectedSiteId === site.id }">
                <font-awesome-layers class="fa-1x me-2">
                  <font-awesome-icon :icon="['fas', 'warehouse']" />
                </font-awesome-layers>
                <span class="ms-1 text">{{ site.name }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <div class="container-fluid" v-else>
    <div class="alert alert-danger" role="alert">
      <h4 class="alert-heading">
        <font-awesome-layers class="fa-1x me-2">
          <font-awesome-icon :icon="['fas', 'shield']" />
        </font-awesome-layers>
        {{ $t('error.pageAccessDenied') }}
      </h4>
      <span>{{ $t('validation.requiredPermissions', { 'permissions': 'System Admin, Tenant Admin' }) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { toast } from 'vue3-toastify';
import * as Yup from 'yup';
import { Form, Field, GenericObject } from 'vee-validate';
import { useSessionStore, useTenantsStore, useSitesStore } from '@/stores';
import CreateTenantRequest from '@/models/requests/CreateTenantRequest';
import UpdateTenantRequest from '@/models/requests/UpdateTenantRequest';
import CreateSiteRequest from '@/models/requests/CreateSiteRequest';
import UpdateSiteRequest from '@/models/requests/UpdateSiteRequest';
import UnsavedChangesModal from '@/components/UnsavedChangesModal.vue';
import { useUnsavedChangesGuard } from '@/services/unsavedChangesGuard';

type FormRef = { meta?: { dirty?: boolean }; resetForm: (state?: { values?: GenericObject }) => void } | null;
const tenantFormRef = ref<FormRef>(null);
const siteFormRef = ref<FormRef>(null);
const unsavedChangesModal = ref<InstanceType<typeof UnsavedChangesModal> | null>(null);

const hasUnsavedChanges = computed(() =>
  Boolean(tenantFormRef.value?.meta?.dirty) || Boolean(siteFormRef.value?.meta?.dirty),
);

useUnsavedChangesGuard(() => hasUnsavedChanges.value, unsavedChangesModal);

const i18n = useI18n();

const sessionStore = useSessionStore();
const { userTenants, userSites, selectedTenantId, selectedSiteId, selectedTenant, selectedSite, isSystemAdmin, isTenantAdmin } = storeToRefs(sessionStore);

const sortedTenants = computed(() => {
  return [...userTenants.value]?.sort((a, b) => a.name.localeCompare(b.name));
});

const tenantFilteredUserSites = computed(() => {
  if (selectedTenantId.value === 0) return [];
  return [...userSites.value]
    ?.filter((site) => site.tenant.id === selectedTenantId.value)
    .sort((a, b) => a.name.localeCompare(b.name));
});

const select_tenant = async (id: number) => await sessionStore.selectTenant(id);
const select_site = async (id: number) => await sessionStore.selectSite(id);

const tenantSchema = Yup.object().shape({
  code: Yup.string().required(i18n.t('validation.required')).max(20, i18n.t('validation.maxLength', { max: 20 })),
  name: Yup.string().required(i18n.t('validation.required')).max(40, i18n.t('validation.maxLength', { max: 40 })),
});

const siteSchema = Yup.object().shape({
  code: Yup.string().required(i18n.t('validation.required')).max(20, i18n.t('validation.maxLength', { max: 20 })),
  name: Yup.string().required(i18n.t('validation.required')).max(40, i18n.t('validation.maxLength', { max: 40 })),
  companyName: Yup.string().notRequired().max(100, i18n.t('validation.maxLength', { max: 100 })),
  address: Yup.string().notRequired().max(400, i18n.t('validation.maxLength', { max: 400 })),
  contactName: Yup.string().notRequired().max(100, i18n.t('validation.maxLength', { max: 100 })),
  email: Yup.string().email(i18n.t('validation.emailRequired')).notRequired().max(256, i18n.t('validation.maxLength', { max: 256 })),
  phone: Yup.string().notRequired().max(20, i18n.t('validation.maxLength', { max: 20 })),
});

async function onSubmitTenant(values: GenericObject, { setErrors, resetForm }: GenericObject) {
  const tenantStore = useTenantsStore();
  const { code, name } = values;

  try {
    if (selectedTenantId.value === 0) {
      const requestBody = new CreateTenantRequest(code, name);
      await tenantStore.create(requestBody);
      toast.success(i18n.t('page.manage-tenants.tenantCreated', { name: name }), { autoClose: 3000 });
    }
    else {
      const requestBody = new UpdateTenantRequest(code, name);
      await tenantStore.update(selectedTenantId.value, requestBody);
      toast.success(i18n.t('page.manage-tenants.tenantUpdated', { name: name }), { autoClose: 3000 });
    }
    resetForm({ values });
  } catch (error) {
    return setErrors({ apiError: error });
  }
}

async function onSubmitSite(values: GenericObject, { setErrors, resetForm }: GenericObject) {
  const siteStore = useSitesStore();
  const { code, name, companyName, address, contactName, email, phone } = values;

  if (selectedTenant.value === undefined) {
    return;
  }

  try {
    if (selectedSiteId.value === 0) {
      const requestBody = new CreateSiteRequest(selectedTenant.value.id, code, name, companyName, address, contactName, email, phone);
      await siteStore.create(requestBody);
      toast.success(i18n.t('page.manage-tenants.siteCreated', { name: name }), { autoClose: 3000 });
    }
    else {
      if (selectedSite.value === undefined) {
        return;
      }
      const requestBody = new UpdateSiteRequest(code, name, companyName, address, contactName, email, phone);
      await siteStore.update(selectedSiteId.value, requestBody);
      toast.success(i18n.t('page.manage-tenants.siteUpdated', { name: name }), { autoClose: 3000 });
    }
    resetForm({ values });
  } catch (error) {
    return setErrors({ apiError: error });
  }
}
</script>
